import { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDashboardStats } from 'api/hooks';
import { Paper, Skeleton } from '@mantine/core';
import { DonutChart } from '@mantine/charts';
import _ from 'lodash';
import { formatPatientsCount } from 'utils';
import { useTranslation } from 'react-i18next';
import { ChartTitle, StatsLabelValue } from './components';

export default function NonInsurancePaymentsStats({ title, queryKey, filter }) {
  const { stats, isLoading } = useDashboardStats({ queryKey, ...filter });
  const { t } = useTranslation();
  const chartData = useMemo(() => {
    if (!_.isEmpty(stats)) {
      const colors = ['indigo.5', 'yellow.4', 'red.4', 'emerald.5', 'sky.5', 'blue.8', 'violet.7'];
      const legendColors = [
        'bg-indigo-500',
        'bg-yellow-400',
        'bg-red-400',
        'bg-emerald-500',
        'bg-sky-500',
        'bg-blue-800',
        'bg-violet-700',
      ];
      const data = _.map(stats, (c, index) => ({
        name: c.mainName,
        value: c.total ?? 0,
        color: colors[index],
        legendColor: legendColors[index],
      }));
      const hasData = _.sumBy(data, 'value') > 0;
      return hasData ? data : [{ key: 'noData', name: t('messages.no-data'), value: 1, color: 'zinc.3' }];
    }
    return [];
  }, [stats, t]);

  const hasData = _.sumBy(chartData, 'value') > 0;
  const formatTooltipValue = useCallback(
    (value) => {
      const total = chartData.reduce((sum, entry) => sum + entry.value, 0);
      const percentage = _.defaultTo((value / total) * 100, 0).toFixed(1);
      return `${formatPatientsCount(t, value)}, ${percentage}%`;
    },
    [chartData, t],
  );
  return (
    <Skeleton visible={isLoading} className="h-full relative hover:z-20">
      <Paper shadow="xs" radius="6px" className="px-4 py-6 w-full h-full">
        <div className="flex flex-col space-y-4">
          <ChartTitle title={title} />
          <div className="flex flex-wrap items-center gap-x-6 gap-y-4">
            <DonutChart
              className="flex-none"
              data={chartData}
              thickness={30}
              withTooltip={hasData}
              tooltipProps={{
                allowEscapeViewBox: { x: true, y: true },
                wrapperStyle: { zIndex: 50, width: 'max-content', maxWidth: 360 },
              }}
            />
            <div className="flex-1 min-w-[260px] space-y-1.5">
              {_.map(chartData, (item) => {
                return (
                  <div className="flex space-x-2 items-center" key={item.name}>
                    <div className={`w-4 h-4 flex-none rounded-full ${item.legendColor}`} />
                    <StatsLabelValue label={item.name} value={formatTooltipValue(item.value)} />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Paper>
    </Skeleton>
  );
}
NonInsurancePaymentsStats.propTypes = {
  filter: PropTypes.object,
  queryKey: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
};
NonInsurancePaymentsStats.defaultProps = {
  filter: {},
};
